import React from 'react';
import { FadeInUp, SectionLabel, AccentLine } from './AnimationWrappers';

const SustainabilitySection = () => {
  return (
    <section id="sustainability" data-testid="sustainability-section" className="relative overflow-hidden" style={{ background: '#F0F7F2', padding: '80px 0' }}>
      <div className="container-main">
        <SectionLabel number="09" title="Sustainability" />
        <FadeInUp>
          <div className="accent-line" style={{ background: '#2D8B4E' }} />
          <h2 className="font-display mb-4" style={{ fontSize: 'clamp(1.8rem, 4vw, 2.6rem)', fontWeight: 700, color: '#1C1C1E' }}>
            Green Manufacturing. <span style={{ color: '#2D8B4E' }}>By Design.</span>
          </h2>
          <p className="font-body mb-14" style={{ fontSize: '1rem', color: '#3A3A3C', maxWidth: '650px', lineHeight: 1.8 }}>
            Sustainability at Qualiance isn't a label we apply after the fact — it's infrastructure we've built into the factory from the ground up. Solar energy, rainwater harvesting, water treatment, and waste segregation aren't initiatives. They're how we operate.
          </p>
        </FadeInUp>

        {/* === Primary: Solar Array — wide card with image + content === */}
        <FadeInUp delay={0.1}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 mb-5 overflow-hidden" style={{ borderRadius: '14px', border: '1px solid rgba(45,139,78,0.15)' }}>
            <div className="overflow-hidden" style={{ minHeight: '300px' }}>
              <img
                src="/sustain-solar.jpg"
                alt="72kW rooftop solar panel array at Qualiance Tiruppur facility"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex flex-col justify-center" style={{ background: '#FFFFFF', padding: 'clamp(1.5rem, 3vw, 2.5rem)' }}>
              <span className="font-mono block mb-3" style={{ fontSize: '0.6rem', color: '#2D8B4E', letterSpacing: '0.15em', textTransform: 'uppercase', fontWeight: 600 }}>Energy Independence</span>
              <h3 className="font-display mb-3" style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1C1C1E' }}>Rooftop Solar Array</h3>
              <p className="font-body mb-5" style={{ fontSize: '0.9rem', color: '#3A3A3C', lineHeight: 1.7 }}>
                Our 72kW rooftop solar installation spans the entire facility roof, generating clean energy for production needs. Grid-tied and self-sufficient — reducing our carbon footprint every single day while keeping production costs competitive.
              </p>
              <div className="flex flex-wrap gap-2 mb-5">
                {['Solar Powered', 'Grid-Tied', 'Carbon Reduction', '72kW Capacity'].map((tag, i) => (
                  <span key={i} className="sustainability-tag">{tag}</span>
                ))}
              </div>
              <div className="flex gap-8">
                {[
                  { val: '72kW', label: 'Installed Capacity' },
                  { val: '40%', label: 'Carbon Reduction' },
                ].map((s, i) => (
                  <div key={i}>
                    <span className="font-display block" style={{ fontSize: '1.3rem', fontWeight: 700, color: '#2D8B4E' }}>{s.val}</span>
                    <span className="font-mono" style={{ fontSize: '0.5rem', color: '#6B6B6F', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{s.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </FadeInUp>

        {/* === Secondary row: Rainwater + Waste Segregation === */}
        <FadeInUp delay={0.2}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
            {/* Rainwater Harvesting */}
            <div style={{ background: '#FFFFFF', borderRadius: '14px', border: '1px solid rgba(45,139,78,0.15)', overflow: 'hidden' }}>
              <div className="overflow-hidden" style={{ height: '240px' }}>
                <img src="/sustain-rainwater.jpg" alt="Building-integrated rainwater harvesting system with collection pipes" className="w-full h-full object-cover" />
              </div>
              <div style={{ padding: '1.5rem' }}>
                <h3 className="font-display mb-2" style={{ fontSize: '1.15rem', fontWeight: 700, color: '#1C1C1E' }}>Rainwater Harvesting</h3>
                <p className="font-body mb-4" style={{ fontSize: '0.85rem', color: '#3A3A3C', lineHeight: 1.65 }}>
                  Building-integrated collection and filtration system across every floor. Harvested rainwater is channelled for non-potable factory use — reducing municipal water dependency and ensuring resource efficiency even during Tiruppur's dry months.
                </p>
                <div className="flex flex-wrap gap-2">
                  {['Water Conservation', 'Multi-Floor Collection', 'Filtration System'].map((tag, i) => (
                    <span key={i} className="sustainability-tag">{tag}</span>
                  ))}
                </div>
              </div>
            </div>

            {/* Waste Segregation */}
            <div style={{ background: '#FFFFFF', borderRadius: '14px', border: '1px solid rgba(45,139,78,0.15)', overflow: 'hidden' }}>
              <div className="overflow-hidden" style={{ height: '240px' }}>
                <img src="/sustain-waste.jpg" alt="Color-coded waste segregation bins — paper, plastic, and organic waste" className="w-full h-full object-cover" style={{ objectPosition: 'center bottom' }} />
              </div>
              <div style={{ padding: '1.5rem' }}>
                <h3 className="font-display mb-2" style={{ fontSize: '1.15rem', fontWeight: 700, color: '#1C1C1E' }}>Waste Segregation & Recycling</h3>
                <p className="font-body mb-4" style={{ fontSize: '0.85rem', color: '#3A3A3C', lineHeight: 1.65 }}>
                  Color-coded waste bins across every department and production floor. Paper, plastic, and organic waste segregated at source. Fabric scraps recycled, hazardous waste tracked and disposed responsibly — working toward a zero-landfill target for all production waste.
                </p>
                <div className="flex flex-wrap gap-2">
                  {['Zero Landfill Target', 'Source Segregation', 'Fabric Recycling'].map((tag, i) => (
                    <span key={i} className="sustainability-tag">{tag}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </FadeInUp>

        {/* === Third row: Water Treatment + Impact Stats === */}
        <FadeInUp delay={0.3}>
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_0.8fr] gap-5">
            {/* Water Treatment */}
            <div style={{ background: '#FFFFFF', borderRadius: '14px', border: '1px solid rgba(45,139,78,0.15)', overflow: 'hidden' }}>
              <div className="grid grid-cols-1 md:grid-cols-2">
                <div className="overflow-hidden" style={{ minHeight: '240px' }}>
                  <img src="/sustain-water-treatment.jpg" alt="Multi-stage water treatment and RO purification plant" className="w-full h-full object-cover" />
                </div>
                <div className="flex flex-col justify-center" style={{ padding: '1.5rem' }}>
                  <h3 className="font-display mb-2" style={{ fontSize: '1.15rem', fontWeight: 700, color: '#1C1C1E' }}>Water Treatment Plant</h3>
                  <p className="font-body mb-4" style={{ fontSize: '0.85rem', color: '#3A3A3C', lineHeight: 1.65 }}>
                    Multi-stage filtration and RO purification for both process water and employee drinking water. Reduces dependency on external supply while ensuring every drop used in production meets quality standards.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {['Multi-Stage RO', 'Process Grade', 'Drinking Water'].map((tag, i) => (
                      <span key={i} className="sustainability-tag">{tag}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Impact Stats */}
            <div className="flex flex-col justify-center" style={{
              background: '#FFFFFF', borderRadius: '14px', border: '1px solid rgba(45,139,78,0.15)', padding: '2rem',
            }}>
              <h3 className="font-display mb-2" style={{ fontSize: '1.2rem', fontWeight: 700, color: '#1C1C1E' }}>Our Environmental Impact</h3>
              <p className="font-body mb-6" style={{ fontSize: '0.82rem', color: '#6B6B6F', lineHeight: 1.6 }}>
                Every investment in sustainability is an investment in manufacturing longevity. These numbers reflect real infrastructure, not pledges.
              </p>
              <div className="space-y-4">
                {[
                  { value: '72kW', label: 'Solar Energy Production', desc: 'Rooftop solar installation capacity' },
                  { value: '95%', label: 'Material Utilization', desc: 'Industry-leading waste reduction' },
                  { value: '40%', label: 'Carbon Footprint Reduction', desc: 'Year-over-year improvement' },
                ].map((stat, i) => (
                  <div key={i} className="flex items-start gap-4" style={{ borderTop: i > 0 ? '1px solid rgba(45,139,78,0.1)' : 'none', paddingTop: i > 0 ? '12px' : 0 }}>
                    <span className="font-display shrink-0" style={{ fontSize: '1.5rem', fontWeight: 700, color: '#2D8B4E', width: '60px' }}>
                      {stat.value}
                    </span>
                    <div>
                      <span className="font-body block" style={{ fontSize: '0.85rem', fontWeight: 600, color: '#1C1C1E' }}>{stat.label}</span>
                      <span className="font-body" style={{ fontSize: '0.75rem', color: '#6B6B6F' }}>{stat.desc}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </FadeInUp>
      </div>
      <div className="section-number" style={{ color: 'rgba(45,139,78,0.06)' }}>09</div>
    </section>
  );
};

export default SustainabilitySection;
